import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, User, MessageSquare, Package, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { whatsappConversationAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useModuleConfig, MODULE_STATUS } from '../context/ModuleConfigContext';
import { onForegroundMessage } from '../services/pushNotificationService';

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { isVisible, isLocked, getStatus } = useModuleConfig();
  const [unread, setUnread] = useState(0);

  const chatsVisible = isVisible('chats');
  // iter139 — Chats module is in `coming_soon`. Keep the tab visible (users
  // can still tap and see the Coming Soon screen) but SKIP unread polling,
  // FCM subscription, and badge render.
  const chatsStatus = getStatus('chats');
  const chatsLive = chatsVisible && chatsStatus === MODULE_STATUS.ENABLED;

  const fetchUnread = React.useCallback(async () => {
    try {
      const res = await whatsappConversationAPI.unreadTotal();
      const n = res?.RESULT?.unread_total || 0;
      setUnread(n);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !chatsLive) { setUnread(0); return; }
    fetchUnread();
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') fetchUnread();
    }, 30000);
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchUnread(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, chatsLive, location.pathname, fetchUnread]);

  useEffect(() => {
    if (!isAuthenticated || !chatsLive) return undefined;
    let unsub = null;
    (async () => {
      unsub = await onForegroundMessage((payload) => {
        const type = payload?.data?.type;
        if (type === 'guest_message' || type === 'guest_cancel_request') {
          fetchUnread();
        }
      });
    })();
    return () => { if (unsub && typeof unsub === 'function') unsub(); };
  }, [isAuthenticated, chatsLive, fetchUnread]);

  // iter188 — Bottom nav order per product spec: Calendar, Rate & Inventory,
  // Chats, Profile. Analytics has been moved to the top-left profile menu
  // (AppHeader → "Analytics") and is intentionally NOT present here.
  const navRegistry = [
    { key: 'calendar',       path: '/',              icon: Calendar,       label: t('calendar') },
    { key: 'rate_inventory', path: '/rate-inventory', icon: Package,       label: 'Rate & Inventory' },
    // Chats module: icon renders whenever chats module isn't fully DISABLED.
    ...(chatsVisible && chatsStatus !== MODULE_STATUS.DISABLED
      ? [{ key: 'chats', path: '/conversations', icon: MessageSquare, label: 'Chats',
           badge: chatsLive ? unread : 0 }]
      : []),
    { key: 'profile',        path: '/profile',        icon: User,          label: t('profile') },
  ];

  const navItems = navRegistry.filter((item) => isVisible(item.key));

  const handleClick = (item) => {
    if (isLocked(item.key)) {
      toast.message('Please upgrade your plan', {
        description: `${item.label} is included in a higher plan.`,
      });
      return;
    }
    navigate(item.path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-[#1B2A7B] border-t border-[#152163] px-2 py-2 shadow-[0_-4px_16px_rgba(11,24,82,0.25)] z-30 safe-area-bottom" data-testid="bottom-navigation">
      <div className="flex items-center justify-around max-w-lg mx-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const locked = isLocked(item.key);
          const isActive = !locked && (
            location.pathname === item.path ||
            (item.path === '/' && location.pathname === '/calendar') ||
            (item.path === '/conversations' && location.pathname.startsWith('/conversations')) ||
            (item.path === '/rate-inventory' && location.pathname.startsWith('/rate-inventory'))
          );

          return (
            <button
              key={item.key}
              onClick={() => handleClick(item)}
              className={`relative flex flex-col items-center py-2 px-3 rounded-xl transition-all ${
                isActive
                  ? 'text-white'
                  : 'text-white/60 hover:text-white/80'
              }`}
              data-testid={`nav-${item.key.replace(/_/g, '-')}`}
              aria-label={locked ? `${item.label} (locked)` : item.label}
            >
              <div className={`relative p-2 rounded-lg transition-colors ${
                isActive ? 'bg-white/15' : ''
              }`}>
                <Icon className={`w-5 h-5 ${isActive ? 'text-white' : ''} ${locked ? 'opacity-70' : ''}`} />
                {locked && (
                  <span
                    className="absolute -top-1 -right-1 bg-white text-[#1B2A7B] rounded-full w-3.5 h-3.5 flex items-center justify-center shadow"
                    data-testid={`nav-${item.key.replace(/_/g, '-')}-lock`}
                    aria-hidden="true"
                  >
                    <Lock className="w-2 h-2" strokeWidth={3} />
                  </span>
                )}
                {!locked && item.badge > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 leading-none"
                    data-testid={`nav-${item.key.replace(/_/g, '-')}-badge`}
                  >
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </div>
              <span className={`text-xs mt-1 font-medium ${isActive ? 'text-white' : ''}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigation;
